<template>
  <div class="w-full">

    <!-- Settings page header -->
    <div class="sticky top-0 z-20 md:static -mx-5 px-5 md:mx-0 md:px-0 bg-white dark:bg-[#1C2128] md:bg-transparent dark:md:bg-transparent border-b border-gray-100 dark:border-gray-700 md:border-0">
      <div class="flex items-center md:items-start justify-between gap-4 h-14 md:h-auto md:py-5">
        <div class="min-w-0">
          <div class="flex items-center gap-1.5">
            <span class="text-[13px] md:text-sm font-semibold text-gray-400 dark:text-gray-500">Settings</span>
            <svg class="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/>
            </svg>
            <h1 class="text-[13px] md:text-sm font-semibold text-gray-900 dark:text-white leading-none truncate">{{ activeTabLabel }}</h1>
          </div>
          <p class="hidden md:block text-sm text-gray-400 dark:text-gray-500 mt-1">{{ activeTabDescription }}</p>
        </div>
        <a v-if="store.current_org.url" :href="'/' + store.current_org.url" target="_blank"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors flex-shrink-0">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
          </svg>
          View public profile
        </a>
      </div>
    </div>

    <!-- Body row: settings sidebar + content -->
    <div class="md:flex md:gap-8 md:items-start">

      <!-- ── Desktop settings sidebar ── -->
      <aside class="hidden md:flex md:flex-col w-60 flex-shrink-0 md:sticky md:top-6 md:max-h-[calc(100vh-3rem)] md:overflow-y-auto md:py-5">

        <!-- Org card -->
        <router-link :to="orgPath('profile')" data-turbo-prefetch="false"
          class="flex items-center gap-3 p-2.5 -mx-1 mb-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-700 pb-4">
          <div class="flex-shrink-0">
            <img v-if="store.current_org.profile_picture_url"
              :src="store.current_org.profile_picture_url"
              class="w-9 h-9 rounded-full object-cover"
              alt="org avatar"/>
            <div v-else :class="store.current_org.avatar_color"
              class="w-9 h-9 rounded-full flex items-center justify-center">
              <span class="text-sm font-bold text-white leading-none">
                {{ store.current_org.avatar_name }}
              </span>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
              {{ store.current_org.name }}
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
              {{ store.current_org.url }}
            </p>
          </div>
        </router-link>

        <!-- Nav -->
        <nav class="space-y-0.5">
          <router-link v-for="tab in tabs" :key="tab.path" :to="orgPath(tab.path)" data-turbo-prefetch="false"
            class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="isActive(tab.path)
              ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" v-html="tab.icon"></svg>
            <span class="truncate">{{ tab.label }}</span>
          </router-link>
        </nav>
      </aside>

      <!-- ── Right side: mobile tabs + main ── -->
      <div class="flex-1 min-w-0">

        <!-- Mobile tab bar (hidden on desktop) -->
        <div class="md:hidden sticky top-14 z-10 -mx-5 px-5 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex gap-1.5 py-2.5 overflow-x-auto no-scrollbar">
            <router-link v-for="tab in tabs" :key="tab.path" :to="orgPath(tab.path)" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="isActive(tab.path) ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ tab.label }}
            </router-link>
          </div>
        </div>

        <div class="mt-5 md:mt-0">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useConstants } from 'composables/useConstants.js'

const store = orgStore()
const route = useRoute()
const { I18N } = useConstants()

const canManageMembers = computed(() => {
  const membership = store.user_role_in_org?.users_organizations
  return membership?.role === 'admin' || membership?.org_owner === true
})

const tabs = computed(() => [
  {
    path: 'profile',
    label: I18N.website_content.account_settings.profile,
    description: "Your organization's public profile, branding, and social links",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />'
  },
  {
    path: 'account',
    label: I18N.website_content.account_settings.account,
    description: "Address, tagline, and branding for your organization",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />'
  },
  {
    path: 'members',
    label: I18N.features.organization.members,
    description: canManageMembers.value
      ? "Invite teammates and manage their roles and access"
      : "See who's part of your organization",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />'
  },
  {
    path: 'plan',
    label: I18N.features.organization.plan,
    description: "View and manage your subscription and billing",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />'
  },
  {
    path: 'developers',
    label: I18N.features.organization.developers,
    description: "API keys and developer tools for integrating with your organization",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />'
  },
])

const activeTab = computed(() => tabs.value.find((tab) => isActive(tab.path)))
const activeTabLabel = computed(() => activeTab.value?.label || 'Settings')
const activeTabDescription = computed(() =>
  activeTab.value?.description || "Manage your organization's profile, members, plan, and integrations")

function orgPath(segment) {
  return `/organizations/${store.current_org.url}/${segment}`
}

function isActive(segment) {
  return new RegExp(`/${segment}(/|$)`).test(route.path)
}
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>
